import { Element } from 'domhandler/lib/node';

import { VIDEO_RATIO } from '../constants/description.constant';

export const parseAttributes = (attributes: Record<string, string>) => {
  const validAttributes: Record<string, string> = {};

  for (const attributeName in attributes) {
    if (checkIsValidDomAttribute(attributeName)) {
      validAttributes[attributeName] = attributes[attributeName];
    }
  }

  return validAttributes;
};

export function checkIsValidDomAttribute(name: string) {
  const matched_special_chars = name.match(/[`~!@#$%^&*()_|+=?;:'",.<>{}[\]\\/]/gi) ?? [];
  if (matched_special_chars.length) {
    return false;
  }
  return /^[a-z](-?[a-z0-9]+)*$/gi.test(name);
}

export function checkLinkAttribute(attributeName: string) {
  return attributeName === 'link';
}

export function checkScriptAttribute(attributeName: string) {
  return attributeName === 'script';
}

export function checkIsSwiperLink(domNode: Element) {
  return domNode.attribs.href?.indexOf('swiper') > -1;
}

export function checkIsCrema(domNode: Element) {
  return domNode.attribs.src?.indexOf('.cre.ma/') > -1;
}

export const removeCssImportantSyntax = (domNode: Element) => {
  domNode.attribs.style = domNode.attribs.style.replace(/!important/gi, '');
  return domNode;
};

export const removeCssFontSize = (domNode: Element) => {
  domNode.attribs.style = domNode.attribs.style.replace(/font-size:([^]*?);/, '');
  return domNode;
};

export const iframeFullHeightToAuto = (domNode: Element) => {
  domNode = styleFullHeightToAuto(domNode);
  domNode = attrFullHeightToAuto(domNode);
  return domNode;
};

function styleFullHeightToAuto(domNode: Element) {
  domNode.attribs.style = domNode.attribs.style.replace(/height\s{0,}?:\s{0,}?100%/gi, 'height: auto');
  return domNode;
}

function attrFullHeightToAuto(domNode: Element) {
  if (domNode.attribs.height === '100%') {
    domNode.attribs.height = 'auto';
  }
  return domNode;
}

export function checkVimeoSource(attributeSource: string) {
  const videoRegexps = [/vimeo/];
  return videoRegexps.some((regexp) => regexp.test(attributeSource));
}

export function checkNeedAdjustSource(attributeSource: string) {
  const videoRegexps = [/youtube|youtu\.be/, /vimeo/];
  return videoRegexps.some((regexp) => regexp.test(attributeSource));
}

export function adjustAspectRatio(domNode: Element) {
  domNode = styleResizeVideoRatio(domNode);
  domNode = styleBorderNone(domNode);
  return domNode;
}

function styleResizeVideoRatio(domNode: Element) {
  try {
    const videoRatio = calcIframeYoutubeRatio(domNode);
    const { attrWidth, attrHeight, styleWidth } = getDomNodeSize(domNode);
    const isGreaterThanOne = Number(attrWidth) / Number(attrHeight) > 1;

    domNode.attribs.style = `
      ${domNode.attribs.style ? `${domNode.attribs.style};` : ''}
      ${!attrWidth && !styleWidth ? 'width: 100%;' : ''}
      ${isGreaterThanOne ? `min-height: calc(100vw / (${videoRatio}))` : ''};
    `;
    return domNode;
  } catch (e) {
    console.error(e);
    return domNode;
  }
}

function calcIframeYoutubeRatio(domNode: Element) {
  const { attrWidth, styleWidth, attrHeight, styleHeight } = getDomNodeSize(domNode);

  if (checkIsFullWidth(attrWidth, styleWidth)) {
    return VIDEO_RATIO;
  }

  if (attrWidth && attrHeight) {
    return `${attrWidth} / ${attrHeight}`;
  }

  if (styleWidth && styleHeight) {
    return `${styleWidth} / ${styleHeight}`;
  }

  return VIDEO_RATIO;
}

function getDomNodeSize(domNode: Element) {
  return {
    attrWidth: domNode.attribs.width,
    attrHeight: domNode.attribs.height,
    styleWidth: domNode.attribs.style.match(/width:([^]*?);/)?.[1] || '',
    styleHeight: domNode.attribs.style.match(/height:([^]*?);/)?.[1] || '',
  };
}

function checkIsFullWidth(width: string, style_width: string) {
  return Boolean(width === '100%' || style_width === '100%');
}

function styleBorderNone(domNode: Element) {
  domNode.attribs.style = `
    ${domNode.attribs.style};
    border: none;
  `;

  return domNode;
}

export function prependUrlToHref(domNode: Element, shopUrl: string) {
  const href = domNode.attribs.href || '';
  if (href.startsWith('/')) {
    domNode.attribs.href = `${shopUrl}${href}`;
  }

  return domNode;
}

export function prependUrlToImgSrc(domNode: Element, shopUrl: string) {
  const lowerCaseAttribs = convertKeysToLowerCase(domNode.attribs);
  const imageSource = lowerCaseAttribs.src || '';

  if (imageSource.startsWith('/') && !imageSource.startsWith('//')) {
    domNode.attribs['data-src'] = `${shopUrl}${imageSource}`;
  } else {
    domNode.attribs['data-src'] = imageSource;
  }
  domNode.attribs.class = 'lazy';
  domNode.attribs.src = '';

  return setAttrReferrerPolicy(domNode);
}

export function setAttrReferrerPolicy(domNode: Element) {
  domNode.attribs.referrerpolicy = 'no-referrer';
  return domNode;
}

export function setAttrAlt(domNode: Element) {
  if (!domNode.attribs.alt) {
    domNode.attribs.alt = '본문 이미지';
  }
  return domNode;
}

export function setVideoAttr(domNode: Element) {
  domNode.attribs['playsinline'] = '';
  return domNode;
}
/**
 * 불필요한 외부 리소스(스크립트, 폰트)를 전달하는 외부 사이트의 경우 지연로딩 처리
 * - 크레마핏
 */
export function shouldLoadLazyIframe(url: string) {
  const regex = /cre.ma/i;

  return regex.test(url);
}

function convertKeysToLowerCase(obj: Record<string, string>) {
  try {
    return Object.keys(obj).reduce((acc: Record<string, string>, key) => {
      acc[key.toLowerCase()] = obj[key];
      return acc;
    }, {});
  } catch (e) {
    console.error(e);
    return obj;
  }
}
